import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import type { Caregiver } from '../types/data.types';
import type { ApiCaregiver } from '../types/api.types';
import { transformCaregiverFromAPI } from '../utils/api-transforms';
import { getVendorFromPartnerUUID } from '../constants/vendor-mapping';

interface UseCaregiversFilter {
  status?: string;
  city?: string;
  search?: string;
}

export function useCaregivers(filters?: UseCaregiversFilter) {
  const [caregivers, setCaregivers] = useState<Caregiver[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isWaitingForVendor, setIsWaitingForVendor] = useState(true);

  // Get partner UUID from Redux auth state
  const partnerUUID = useSelector((state: any) => state.authReducer?.setting?.partnerUUID);

  // Look up vendor name from mapping
  const vendorName = getVendorFromPartnerUUID(partnerUUID);

  // Update waiting state when vendorName becomes available
  useEffect(() => {
    if (vendorName) {
      setIsWaitingForVendor(false);
    }
  }, [vendorName]);

  useEffect(() => {
    const fetchCaregivers = async () => {
      // Wait for vendorName to be available before proceeding
      if (!vendorName) {
        // Keep loading state true while waiting for vendor name
        // Don't set error or loading false here - we're just waiting for Redux to populate
        return;
      }

      try {
        setLoading(true);
        const { homeNursingService } = await import('../../api/homeNursingClient');

        // Log API request details
        console.log('\n═══════════════════════════════════════════════════════');
        console.log('🔵 CAREGIVERS API CALL');
        console.log('═══════════════════════════════════════════════════════');
        console.log('📍 Endpoint: GET /public/caregivers');
        console.log('📦 Partner UUID:', partnerUUID);
        console.log('🏷️  Vendor Name:', vendorName);
        console.log('⚙️  Request Params:', JSON.stringify({
          serviceModel: 'MARKETPLACE',
          vendor: vendorName,
          city: filters?.city,
          search: filters?.search,
        }, null, 2));
        console.log('═══════════════════════════════════════════════════════\n');

        const response = await homeNursingService.getCaregivers({
          serviceModel: 'MARKETPLACE',  // Always MARKETPLACE for these partners
          vendor: vendorName,            // Partner-specific vendor name
          // Don't pass status filter - fetch all caregivers
          city: filters?.city,
          search: filters?.search,
        });

        // Log API response details
        console.log('\n═══════════════════════════════════════════════════════');
        console.log('✅ CAREGIVERS API RESPONSE');
        console.log('═══════════════════════════════════════════════════════');
        console.log('📊 Success:', response.success);
        console.log('📈 Total Count:', response.data?.length || 0);
        console.log('\n📋 Full Response:');
        console.log(JSON.stringify(response, null, 2));

        if (response.data && response.data.length > 0) {
          console.log('\n👤 Sample Caregivers:');
          console.log('First caregiver:', JSON.stringify(response.data[0], null, 2));
          if (response.data.length > 1) {
            console.log('Second caregiver:', JSON.stringify(response.data[1], null, 2));
          }
          if (response.data.length > 2) {
            console.log('Last caregiver:', JSON.stringify(response.data[response.data.length - 1], null, 2));
          }
        }
        console.log('═══════════════════════════════════════════════════════\n');

        // Transform API response to Caregiver[]
        const transformed = response.data.map((apiCaregiver: ApiCaregiver) =>
          transformCaregiverFromAPI(apiCaregiver)
        );

        setCaregivers(transformed);
        setError(null);
      } catch (err) {
        setError(err as Error);
        console.error('Failed to fetch caregivers:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCaregivers();
  }, [filters?.status, filters?.city, filters?.search, vendorName, partnerUUID]);

  return { caregivers, loading, error, isWaitingForVendor };
}
